import React from "react";
import { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { Location as TQSLocation } from "../../models/location";
import { QualityAlert } from "../../models/quality-alert";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTriangleExclamation,
  faCircleExclamation,
  faEllipsis,
  faFilter,
  faTimes,
  faClose,
} from "@fortawesome/free-solid-svg-icons";
import QualityAlertDetails from "./qualityAlertDetails";
import { TimeSpanSelector } from "../../components/ui/time-span-selector";
import { useSearchParams } from "react-router-dom";
import Select, { SingleValue } from "react-select";
import { useModal } from "../../components/hooks/useModal";
import { useDispatch, useSelector } from "react-redux";
import {
  clearQualityAlerts,
  fetchQualityAlertsByLocation,
  updateQualityAlert,
} from "../../reducers/qualityAlertSlice";

import type { AppDispatch, RootState } from "../../reducers/store";
import { shallowEqual } from "react-redux";

import { AddQualityAlertModal } from "../../components/ui/AddQualityAlertModal/addQualityAlertModal";

const modalIdentifier = "qualityIssueFeedFilterModal";

interface QualityIssuesCardProps {
  selectedLocation: TQSLocation | null;
  allLocations: TQSLocation[];
  locationsLoaded: boolean;
}

type QueryOptions = {
  before: string;
  after: string;
  notStatus: string;
};

// Function for Formatting the Date for rows
const formatDate = (date: Date | null): string => {
  try {
    if (!date) return "";
    if (isNaN(date.getTime())) return "";

    const options: Intl.DateTimeFormatOptions = {
      month: "long", // Full name of the month
      day: "2-digit", // Two-digit day
      year: "numeric", // Full year
      hour: "numeric", // Numeric hour without leading zero
      minute: "2-digit", // Two-digit minute
      second: "2-digit", // Two-digit second
      hour12: true, // 12-hour format with AM/PM
      timeZone: "America/New_York", // EST/EDT timezone
      timeZoneName: "short", // Short name of the timezone
    };

    const formattedDate = new Intl.DateTimeFormat(
      "en-US",
      options,
    ).formatToParts(date);

    const day = formattedDate.find((part) => part.type === "day")?.value;
    const month = formattedDate.find((part) => part.type === "month")?.value;
    const year = formattedDate.find((part) => part.type === "year")?.value;
    const hour = formattedDate.find((part) => part.type === "hour")?.value;
    const minute = formattedDate.find((part) => part.type === "minute")?.value;
    const second = formattedDate.find((part) => part.type === "second")?.value;
    const dayPeriod = formattedDate.find(
      (part) => part.type === "dayPeriod",
    )?.value;
    const timeZone = formattedDate.find(
      (part) => part.type === "timeZoneName",
    )?.value;

    return ` ${day} ${month} ${year} ${hour}:${minute} ${dayPeriod} ${timeZone}`;
  } catch (error) {
    console.error("Error formatting date", error);
    return "";
  }
};

// // FUNCTION TO APPLY FILTERS TO QUALITY ALERTS

// // FOR CALCULATING DATE RANGE IN THE API
// const calculateDateRange = (dateRange: string) => {
//   const endDate = new Date();
//   const startDate = new Date();
//   const days = parseInt(dateRange, 10);

//   if (!isNaN(days)) {
//     startDate.setDate(endDate.getDate() - days);
//   } else {
//     switch (dateRange) {
//       case "30d":
//         startDate.setDate(endDate.getDate() - 30);
//         break;
//       case "60d":
//         startDate.setDate(endDate.getDate() - 60);
//         break;
//       case "90d":
//         startDate.setDate(endDate.getDate() - 90);
//         break;
//       default:
//         startDate.setDate(endDate.getDate() - 30); // Default to last 30 days if no range is selected
//         break;
//     }
//   }

//   return {
//     before: endDate,
//     after: startDate,
//   };
// };

// Quality Issues Card

export default function QualityIssuesCard({
  selectedLocation,
  allLocations,
  locationsLoaded,
}: QualityIssuesCardProps) {
  const selectedSupplier = useSelector((state: RootState) => state.supplier.selectedSupplier);
  const selectedProducingFacilities = useSelector((state: RootState) => state.supplier.selectedProducingFacilities);

  const dispatch = useDispatch<AppDispatch>();
  const [modalOn, setModal] = useState(false);
  const modalRef = useRef<any>();
  const [selectedDays, setSelectedDays] = useState("90d");
  const [showCustomDateRangeModal, setShowCustomDateRangeModal] =
    useState(false);
  const [isCustomDateFilterActive, setIsCustomDateFilterActive] =
    useState(false);
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");
  const [dateFilter, setDateFilter] = useState("90d");
  // const [qualityAlerts, setQualityAlerts] = useState<QualityAlert[]>([]);
  // const [filteredQualityAlerts, setFilteredQualityAlerts] = useState<
  //   QualityAlert[]
  // >([]);
  const [customDateRange, setCustomDateRange] = useState(0);
  const [filters, setFilters] = useState({
    status: null,
    prrNumber: "",
    qaNumber: "",
    partNumber: "",
    locationCount: "",
    type: "",
  });
  const [tempFilters, setTempFilters] = useState({
    status: null,
    prrNumber: "",
    qaNumber: "",
    partNumber: "",
    locationCount: "",
    type: "",
  });
  const [Loading, setLoading] = useState(false);
  const [expandedRow, setExpandedRow] = useState<number | null>(null);
  const [maxHeight, setMaxHeight] = useState<string>("0px");
  const contentRef = useRef<HTMLDivElement>(null);

  const [dateDetectedSort, setDateDetectedSort] = useState<
    "asc" | "desc" | null
  >("desc");
  const [locationsAffectedSort, setLocationsAffectedSort] = useState<
    "asc" | "desc" | null
  >(null);

  const typeOptions = [
    { label: "All Issues", value: null },
    { label: "NCT Issued", value: "nct issued" },
    { label: "Avoided NCT", value: "avoided nct" },
  ];

  const statusOptions = [
    { label: "All", value: null },
    { label: "Open", value: "Open" },
    { label: "Closed", value: "Closed" },
  ];
  const qualityAlerts = useSelector((state: RootState) => {
    return Object.values(state.qa.models);
  }, shallowEqual);

  const [partList, setPartList] = useState<any[]>([]);

  const [searchParams] = useSearchParams();

  const [open, setOpen, isOpen] = useModal();

  const reduxLoading = useSelector((state: RootState) => state.qa.loading);
  const error = useSelector((state: RootState) => state.qa.error);

  const [isTableLoading, setIsTableLoading] = useState(false);

  const [isLoadingData, setIsLoadingData] = useState(false);

  useEffect(() => {
    if (selectedSupplier?.id) {
      setLoading(true);
    }
  }, [selectedSupplier?.id]);

  // Clear filters
  const handleClearFilters = () => {
    setFilters({
      status: null,
      prrNumber: "",
      qaNumber: "",
      partNumber: "",
      locationCount: "",
      type: "",
    });
  };
  // check for Filters
  const isAnyFilterActive = Object.entries(filters).some(([key, value]) => {
    if (key === 'status') {
      return value !== null; // status is active if it's not null (i.e., "Open" or "Closed")
    }
    return value !== ""; // other filters are active if they're not empty
  });

  const clearPreviousParams = () => {
    const params = new URLSearchParams(window.location.search);
    params.delete("qaNumber");

    return params;
  };

  // setting height for dropdown
  const toggleRow = (index: number) => {
    if (expandedRow === index) {
      setExpandedRow(null);
      const params = clearPreviousParams();

      window.history.replaceState(
        {},
        "",
        `${window.location.pathname}${params.toString().length > 0 ? "?" : ""}${params.toString()}`,
      );
    } else {
      setExpandedRow(index);
      const selectedQa = qualityAlerts[index];
      const params = clearPreviousParams();

      params.append("qaNumber", selectedQa.id.toString());

      window.history.replaceState(
        {},
        "",
        `${window.location.pathname}${params.toString().length > 0 ? "?" : ""}${params.toString()}`,
      );

      // Using scrollIntoView to ensure the expanded content is visible
      setTimeout(() => {
        const element = document.getElementById(`quality-alert-row-${index}`);
        if (element) {
          element.scrollIntoView({
            behavior: "smooth",
            block: "start", // Ensure the element is centered in the viewport
          });
        }
      }, 300); // Adjust delay to match the expansion transition duration
    }
  };

  // setting height for dropdown
  useEffect(() => {
    if (contentRef.current && expandedRow !== null) {
      setMaxHeight(`${contentRef.current.scrollHeight * 2}px`);
    } else {
      setMaxHeight("0px");
    }
  }, [expandedRow]);

  useEffect(() => {
    if (!locationsLoaded) {
      return;
    }

    const locationIds = selectedLocation?.id
      ? [selectedLocation.id]
      : allLocations.map((loc) => loc.id);

    if (!locationIds.length || !selectedSupplier?.id) {
      return;
    }

    // Set loading state before dispatch
    setIsLoadingData(true);
    
    dispatch(
      fetchQualityAlertsByLocation({
        locationIds,
        dateFilter,
        useCache: dateFilter === "30d",
        supplierId: selectedSupplier.id,
      }),
    );

    // Cleanup function to clear alerts when component unmounts
    return () => {
      dispatch(clearQualityAlerts());
    };
  }, [
    selectedLocation,
    allLocations,
    selectedSupplier?.id,
    dateFilter,
    locationsLoaded,
  ]);

  // Add separate effect to handle loading state completion
  useEffect(() => {
    if (!reduxLoading && isLoadingData) {
      setIsLoadingData(false);
    }
  }, [reduxLoading]);

  const applyFiltersToAlerts = (alerts: QualityAlert[], filters: any) => {
    return alerts.sort((a, b) => new Date(b.createTime || "").getTime() - new Date(a.createTime || "").getTime()).filter((alert) => {
      if (selectedSupplier.id && alert.supplierId !== selectedSupplier.id && !selectedProducingFacilities.map((facility) => facility.id).includes(alert.supplierId)) {
        return false;
      }

      const matchesPrrNumber = filters.prrNumber
        ? alert.prrNumber?.includes(filters.prrNumber)
        : true;
      const matchesQaNumber = filters.qaNumber
        ? alert.number.includes(filters.qaNumber)
        : true;
      const matchesPartNumber = filters.partNumber
        ? alert.qualityAlertLocationInfo.some((info) =>
            info.qualityAlertDiscrepantMaterial.some((material) =>
              material.part.id.toString().includes(filters.partNumber),
            ),
          )
        : true;
      const matchesType = filters.type
        ? (filters.type.toLowerCase() === "nct issued" && alert.level === 1) ||
          (filters.type.toLowerCase() === "avoided nct" && alert.level !== 1)
        : true;

      // Ensure we only count locations with "Issue Found" status
      const uniqueLocationsWithIssueFound = new Set(
        alert.qualityAlertLocationInfo
          .filter((info) => info.status === "Issue Found") // Only include locations with an "Issue Found"
          .map((info) => info.location),
      );

      const locationCount = uniqueLocationsWithIssueFound.size;

      const matchesLocationCount = filters.locationCount
        ? locationCount === Number(filters.locationCount)
        : true;

      const matchesStatus = filters.status
        ? alert.status === filters.status
        : true;

      return (
        matchesPrrNumber &&
        matchesQaNumber &&
        matchesPartNumber &&
        matchesType &&
        matchesLocationCount &&
        matchesStatus
      );
    });
  };

  const getLocationCount = useCallback((issue: QualityAlert) => {
    const uniqueLocations = new Set(
      issue.qualityAlertLocationInfo
        .filter((q) => {
          return (
            q.status === "Issue Found" ||
            (q.location as unknown as TQSLocation).id ===
              (issue.location as unknown as TQSLocation).id
          );
        })
        .map((info) => info.location),
    );
  
    return uniqueLocations.size;
  }, []);

  // Then update the filteredQualityAlerts memo to be more efficient
  const filteredQualityAlerts = useMemo(() => {
    // First apply filters
    const filtered = applyFiltersToAlerts(qualityAlerts, filters);
    
    // Create a new array only if we need to sort
    if (!dateDetectedSort && !locationsAffectedSort) {
      return filtered;
    }
    
    const sorted = [...filtered];
    
    if (dateDetectedSort) {
      sorted.sort((a, b) => {
        const timeA = new Date(a.createTime ?? '').getTime();
        const timeB = new Date(b.createTime ?? '').getTime();
        return dateDetectedSort === "desc" ? timeB - timeA : timeA - timeB;
      });
    } else if (locationsAffectedSort) {
      // Pre-calculate location counts to avoid repeated calculations
      const locationCounts = new Map<number, number>();
      sorted.forEach(alert => {
        locationCounts.set(alert.id, getLocationCount(alert));
      });
      
      sorted.sort((a, b) => {
        const countA = locationCounts.get(a.id) ?? 0;
        const countB = locationCounts.get(b.id) ?? 0;
        return locationsAffectedSort === "desc" ? countB - countA : countA - countB;
      });
    }
    
    return sorted;
  }, [qualityAlerts, filters, dateDetectedSort, locationsAffectedSort, getLocationCount]);

  // Replace the filtering effect with direct usage of memoized results
  // useEffect(() => {
  //   setFilteredQualityAlerts(applyFiltersToAlerts(qualityAlerts, filters));
  // }, [qualityAlerts, filters]);

  // Separate useEffect for URL params
  useEffect(() => {
    if (filteredQualityAlerts.length > 0) {
      openSelectedQAByUrl();
    }
  }, [searchParams, filteredQualityAlerts]); // Only run when URL or filtered alerts change

  const openSelectedQAByUrl = () => {
    setTimeout(() => {
      if (searchParams.get("qaNumber")) {
        const qaNumber = searchParams.get("qaNumber");
        const indexOfQA = filteredQualityAlerts.findIndex(
          (alert) => alert.id === Number(qaNumber),
        );

        if (indexOfQA !== -1) {
          setExpandedRow(indexOfQA);
        }
      }
    }, 100);
  };

  //  OPENENING THE FILTER MODAL, CHECKING IF BUTTON WAS CLICKED TWICE, IF SO DEFAULT TO 30 DAYS, OR APPLY OTHER DAY RANGE
  const handleButtonClick = async (button: string) => {
    if (button === "...") {
      setShowCustomDateRangeModal(true);
      return;
    }

    const newDateFilter = selectedDays === button ? "30d" : button;

    setSelectedDays(newDateFilter);
    setDateFilter(newDateFilter);

    const locationIds = selectedLocation?.id
      ? [selectedLocation.id]
      : allLocations
          .map((loc) => loc.id)
          .filter((id) => id !== undefined && id !== null);

    dispatch(
      fetchQualityAlertsByLocation({
        locationIds,
        dateFilter: newDateFilter,
        useCache: newDateFilter === "30d",
        supplierId: selectedSupplier.id,
      }),
    );
  };

  function toggleModal() {
    setModal(!modalOn);
  }

  // OPEN NEW QUALITY ALERT MODAL
  const addNewButtonClick = () => {};

  const handleTempFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setTempFilters((prevFilters) => ({ ...prevFilters, [name]: value }));
  };

  const applyFilters = () => {
    setFilters(tempFilters);
    setOpen("");
  };

  const clearFilter = () => {
    const updatedFilters = {
      status: null,
      prrNumber: "",
      qaNumber: "",
      partNumber: "",
      locationCount: "",
      type: "",
    };

    setFilters(updatedFilters);
    setTempFilters(updatedFilters);

    setOpen("");
  };

  // HANDLING CUSTOM DATE RANGE SUBMIT
  const handleCustomDateRangeSubmit = async () => {
    // Ensure the start date is before the end date
    if (new Date(customStartDate) > new Date(customEndDate)) {
      alert("Start date must be before the end date");
      return;
    }

    setSelectedDays("custom");
    setDateFilter("custom");

    const locationIds = selectedLocation?.id
      ? [selectedLocation.id]
      : allLocations
          .map((loc) => loc.id)
          .filter((id) => id !== undefined && id !== null);

    const dateRange = {
      before: new Date(customEndDate).toISOString(),
      after: new Date(customStartDate).toISOString(),
    };

    try {
      dispatch(
        fetchQualityAlertsByLocation({
          locationIds,
          dateFilter: dateRange,
          useCache: false,
          supplierId: selectedSupplier.id,
        }),
      );
    } catch (error) {
      console.error("Error fetching quality alerts:", error);
    }

    setShowCustomDateRangeModal(false);
    setIsCustomDateFilterActive(true);
  };

  // HANDLING TIME SPAN CHANGE
  const handleTimeSpanChange = async (
    data: string,
    customStartDate?: string,
    customEndDate?: string,
  ) => {
    if (customStartDate && customEndDate) {
      if (new Date(customStartDate) > new Date(customEndDate)) {
        alert("Start date must be before the end date");
        return;
      }

      setSelectedDays("custom");
      setDateFilter("custom");

      const locationIds = selectedLocation?.id
        ? [selectedLocation.id]
        : allLocations
            .map((loc) => loc.id)
            .filter((id) => id !== undefined && id !== null);

      const dateRange = {
        before: new Date(customEndDate).toISOString(),
        after: new Date(customStartDate).toISOString(),
      };

      try {
        dispatch(
          fetchQualityAlertsByLocation({
            locationIds,
            dateFilter: dateRange,
            useCache: false,
            supplierId: selectedSupplier.id,
          }),
        );
      } catch (error) {
        console.error("Error fetching quality alerts:", error);
      }
    } else if (data !== "custom") {
      setSelectedDays(data);
      setDateFilter(data);

      const locationIds = selectedLocation?.id
        ? [selectedLocation.id]
        : allLocations
            .map((loc) => loc.id)
            .filter((id) => id !== undefined && id !== null);

      await dispatch(
        fetchQualityAlertsByLocation({
          locationIds,
          dateFilter: data,
          useCache: false,
          supplierId: selectedSupplier.id,
        }),
      );
    }
  };

  // const getQualityAlertData = async (
  //   locationIds: number[],
  //   dateFilter: string | { before: string; after: string },
  //   useCache?: boolean,
  // ) => {
  //   const alerts = await fetchQualityAlerts(locationIds, dateFilter, useCache);
  //   buildPartList(alerts);

  //   return alerts;
  // };

  // const buildPartList = (allQualityAlerts: QualityAlert[]) => {
  //   const partList = allQualityAlerts
  //     .map((alert) => alert.qualityAlertLocationInfo)
  //     .flat()
  //     .map((info) => info.qualityAlertDiscrepantMaterial)
  //     .flat()
  //     .map((material) => material.part)
  //     .filter(
  //       (part, index, self) =>
  //         self.findIndex((p) => p.id === part.id) === index,
  //     );

  //   setPartList(partList);
  // };

  const dateDetectedChange = () => {
    setDateDetectedSort(dateDetectedSort === "desc" ? "asc" : "desc");
    setLocationsAffectedSort(null);
  };
  
  const locationsAffectedChange = () => {
    setLocationsAffectedSort(locationsAffectedSort === "desc" ? "asc" : "desc");
    setDateDetectedSort(null);
  };

  const handleQualityAlertUpdate = (qa: QualityAlert) => {
    dispatch(updateQualityAlert(qa));
  };

  return (
    <>
      <div className="relative flex h-[100%] w-full flex-col rounded-[8px] border border-gray-300 bg-white">
        <div className="bg flex flex-shrink-0 flex-row items-center overflow-visible p-4">
          <div className="flex items-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 512 512"
              width="20px"
              height="20px"
              fill="#212322"
            >
              <path d="M256 32c14.2 0 27.3 7.5 34.5 19.8l216 368c7.3 12.4 7.3 27.7 .2 40.1S486.3 480 472 480L40 480c-14.3 0-27.6-7.7-34.7-20.1s-7-27.8 .2-40.1l216-368C228.7 39.5 241.8 32 256 32zm0 128c-13.3 0-24 10.7-24 24l0 112c0 13.3 10.7 24 24 24s24-10.7 24-24l0-112c0-13.3-10.7-24-24-24zm32 224a32 32 0 1 0 -64 0 32 32 0 1 0 64 0z" />
            </svg>
            <span className="ml-[8px] mr-[8px] overflow-hidden text-ellipsis text-nowrap text-[20px] font-semibold text-tqsBlack">
              Quality Issues Feed
            </span>
          </div>
          <div className="relative flex flex-1 justify-end space-x-2">
            <button
              className="flex items-center rounded-[4px] border border-gray-300 bg-white px-[6px] text-tqsBlack shadow-lg"
              onClick={() =>
                setOpen(isOpen(modalIdentifier) ? "" : modalIdentifier)
              }
            >
              <FontAwesomeIcon
                icon={faFilter}
                className="mr-[8px] text-[14px]"
              />
              Filters
            </button>

            {isOpen(modalIdentifier) && (
              <div className="absolute right-[18px] top-[45px] z-[999999999] w-[350px] rounded-md border-[1px] border-[#E5E5E5] bg-[white] p-[12px] shadow-lg">
                <div className="z-50 bg-white">
                  <div className="mb-[8px] flex items-center justify-between border-b-[1px] pb-[4px]">
                    <span className="text-[14px] font-semibold text-tqsBlack">
                      Apply Filters
                    </span>

                    <FontAwesomeIcon
                      className="cursor-pointer"
                      icon={faClose}
                      onClick={() => setOpen("")}
                    />
                  </div>
                  <div className="flex flex-col space-y-2">
                    <Select
                      defaultValue={statusOptions[0]}
                      value={statusOptions.find(
                        (option) => option.value === tempFilters.status,
                      )}
                      onChange={(e) =>
                        handleTempFilterChange({
                          target: { name: "status", value: e?.value as any },
                        } as any)
                      }
                      options={statusOptions}
                      styles={{
                        control: (base, state) => ({
                          ...base,
                          borderColor: state.isFocused ? "#e5e7eb" : "#e5e7eb",
                        }),
                      }}
                    />
                    <input
                      type="text"
                      name="prrNumber"
                      value={tempFilters.prrNumber}
                      onChange={handleTempFilterChange}
                      placeholder="PRR Number"
                      className="border bg-white px-2 py-1 text-tqsBlack"
                    />
                    <input
                      type="text"
                      name="qaNumber"
                      value={tempFilters.qaNumber}
                      onChange={handleTempFilterChange}
                      placeholder="QA Number"
                      className="border bg-white px-2 py-1 text-tqsBlack"
                    />
                    <input
                      type="text"
                      name="locationCount"
                      value={tempFilters.locationCount}
                      onChange={handleTempFilterChange}
                      placeholder="Locations Affected"
                      className="border bg-white px-2 py-1 text-tqsBlack"
                    />
                    {/* <input
                      type="text"
                      name="type"
                      value={tempFilters.type}
                      onChange={handleTempFilterChange}
                      placeholder="Type"
                      className="border bg-white px-2 py-1"
                    /> */}
                    <Select
                      defaultValue={typeOptions[0]}
                      value={typeOptions.find(
                        (option) => option.value === tempFilters.type,
                      )}
                      onChange={(e) =>
                        handleTempFilterChange({
                          target: { name: "type", value: e?.value as any },
                        } as any)
                      }
                      options={typeOptions}
                      styles={{
                        control: (base, state) => ({
                          ...base,
                          borderColor: state.isFocused ? "#e5e7eb" : "#e5e7eb",
                        }),
                      }}
                    />
                    {/* <input
                      type="text"
                      name="partNumber"
                      value={tempFilters.partNumber}
                      onChange={handleTempFilterChange}
                      placeholder="Part Number"
                      className="border bg-white px-2 py-1"
                    /> */}
                    <Select
                      defaultValue={undefined}
                      value={partList
                        .map((part) => ({
                          label: part.number,
                          value: part.id.toString(),
                        }))
                        .find(
                          (option) => option.value === tempFilters.partNumber,
                        )}
                      onChange={(e) =>
                        handleTempFilterChange({
                          target: {
                            name: "partNumber",
                            value: e?.value as any,
                          },
                        } as any)
                      }
                      options={partList.map((part) => ({
                        label: part.number,
                        value: part.id.toString(),
                      }))}
                      styles={{
                        control: (base, state) => ({
                          ...base,
                          borderColor: state.isFocused ? "#e5e7eb" : "#e5e7eb",
                        }),
                      }}
                    />
                  </div>
                  <div className="mt-4 flex justify-between space-x-2">
                    <div>
                      <button
                        onClick={() => clearFilter()}
                        className="rounded-md bg-tqsBlack px-4 py-2 text-[white]"
                      >
                        Clear Filters
                      </button>
                    </div>

                    <div className="flex justify-end space-x-2">
                      <button
                        onClick={() => setOpen("")}
                        className="rounded-md bg-gray-300 px-4 py-2 text-tqsBlack"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={applyFilters}
                        className="rounded-md bg-tqsYellow px-4 py-2 text-white"
                      >
                        Apply
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {isAnyFilterActive && (
              <button
                className="ml-2 flex items-center rounded-[4px] border border-gray-300 bg-white px-[6px] text-tqsBlack shadow-lg"
                onClick={() => clearFilter()}
              >
                <FontAwesomeIcon icon={faTimes} className="mr-2" />
                Clear Filters
              </button>
            )}

            <div className="flex">
              <TimeSpanSelector
                selectedDays={selectedDays}
                customStartDate={customStartDate}
                customEndDate={customEndDate}
                handleButtonClick={handleTimeSpanChange}
              />
            </div>
          </div>
          {/* <button
            className="ml-3 min-w-[100px] overflow-hidden text-ellipsis text-nowrap rounded-md bg-tqsLightRed px-3 py-1 text-[14px] text-tqsRed"
            onClick={() => modalRef.current.openModal()}
          >
            Add New
          </button> */}
        </div>

        {showCustomDateRangeModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center backdrop-blur">
            <div className="z-50 rounded-md bg-white p-6 shadow-lg">
              <h2 className="mb-4 text-lg font-semibold">
                Enter Custom Date Range
              </h2>
              <div className="flex flex-col space-y-2">
                <input
                  type="date"
                  name="customStartDate"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  placeholder="Start Date"
                  className="border bg-white px-2 py-1"
                />
                <input
                  type="date"
                  name="customEndDate"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  placeholder="End Date"
                  className="border bg-white px-2 py-1"
                />
              </div>
              <div className="mt-4 flex justify-end space-x-2">
                <button
                  onClick={() => setShowCustomDateRangeModal(false)}
                  className="rounded-md bg-gray-300 px-4 py-2"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleCustomDateRangeSubmit()}
                  className="rounded-md bg-tqsYellow px-4 py-2 text-white"
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="flex-grow overflow-y-auto rounded-xl">
          <div className="relative">
            {(reduxLoading || isLoadingData) && (
              <div className="absolute inset-0 z-10 mt-48 flex items-center justify-center bg-white bg-opacity-50 backdrop-blur-sm">
                <span className="loading loading-spinner h-[40px] w-[40px] text-tqsYellow"></span>
              </div>
            )}
            <div>
              <table className="w-full table-fixed border-collapse text-tqsBlack">
                <thead className="sticky top-0 z-10 bg-white">
                  <tr className="partial-border relative border-gray-200">
                    <th className="sticky top-0 w-[6%] justify-end bg-white px-[6px] py-3 text-center text-xs font-semibold tracking-wider text-gray-500"></th>
                    <th className="sticky top-0 w-[20%] justify-end bg-white px-[6px] py-3 text-left text-xs font-semibold tracking-wider text-gray-500">
                      Issue Number
                    </th>
                    <th className="sticky top-0 w-[12%] bg-white px-[6px] py-3 text-left text-xs font-semibold tracking-wider text-gray-500">
                      Type
                    </th>
                    <th className="sticky top-0 w-[12%] bg-white px-[6px] py-3 text-left text-xs font-semibold tracking-wider text-gray-500">
                      Part(s) <br /> Affected
                    </th>
                    <th
                      className="sticky top-0 w-[12%] cursor-pointer bg-white px-[6px] py-3 text-left text-xs font-semibold tracking-wider text-gray-500"
                      onClick={() => locationsAffectedChange()}
                    >
                      Locations Affected
                      {locationsAffectedSort && (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={1.5}
                          stroke="currentColor"
                          className={`ml-1 inline h-4 w-4 ${locationsAffectedSort === "desc" ? "" : "rotate-180"}`}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="m19.5 8.25-7.5 7.5-7.5-7.5"
                          />
                        </svg>
                      )}
                    </th>
                    <th
                      className="sticky top-0 w-[20%] cursor-pointer bg-white px-[6px] py-3 text-left text-xs font-semibold tracking-wider text-gray-500"
                      onClick={() => dateDetectedChange()}
                    >
                      Date Detected
                      {dateDetectedSort && (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={1.5}
                          stroke="currentColor"
                          className={`ml-1 inline h-4 w-4 ${dateDetectedSort === "desc" ? "" : "rotate-180"}`}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="m19.5 8.25-7.5 7.5-7.5-7.5"
                          />
                        </svg>
                      )}
                    </th>
                    <th className="sticky top-0 w-[15%] bg-white px-[6px] py-3 text-center text-xs font-semibold tracking-wider text-gray-500">
                      Current Status
                    </th>
                    <th className="sticky top-0 w-[12%] bg-white px-[6px] py-3 text-center text-xs font-semibold tracking-wider text-gray-500"></th>
                  </tr>
                </thead>

                {reduxLoading || isLoadingData ? (
                  <tbody className="bg-white">
                    <tr>
                      <td colSpan={100} className="py-4 text-center">
                        <span className="loading loading-spinner hidden h-[40px] w-[40px] text-tqsYellow"></span>
                      </td>
                    </tr>
                  </tbody>
                ) : (
                  <tbody className="bg-white">
                    {filteredQualityAlerts.length === 0 ? (
                      <tr>
                        <td colSpan={100} className="py-4 text-center text-xl">
                          No Quality Issues Found
                        </td>
                      </tr>
                    ) : (
                      filteredQualityAlerts.map((issue, index) => (
                        <React.Fragment key={issue.id}>
                          <tr
                            id={`quality-alert-row-${index}`}
                            key={index}
                            className="cursor-pointer hover:bg-gray-50"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleRow(index);
                            }}
                          >
                            <td className="px-3 py-4 text-right">
                              <FontAwesomeIcon
                                icon={
                                  issue.status === "high"
                                    ? faTriangleExclamation
                                    : faTriangleExclamation
                                }
                                className={`flex-shrink-0 ${
                                  issue.level == 1
                                    ? "rounded-full border border-tqsRed bg-tqsLightRed px-1.5 py-1.5 text-tqsRed"
                                    : "rounded-full border border-tqsYellow bg-tqsLighterYellow px-1.5 py-1.5 text-tqsYellow"
                                }`}
                                size="sm"
                              />
                            </td>
                            <td className="px-[6px] py-4">
                              <div className="flex items-center space-x-3">
                                <div>
                                  <div className="text-sm font-semibold">
                                    {`PRR ${issue?.prrNumber ? `${issue.prrNumber !== "Unknown" ? "#" : ""}${issue.prrNumber}` : "Unknown"}`}
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    {issue.level === 1
                                      ? `QA ${issue.number}`
                                      : `NM ${issue.number}`}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td
                              className={`px-[6px] py-4 text-left text-sm font-semibold ${
                                issue.level === 1
                                  ? "text-tqsRed"
                                  : "text-tqsYellow"
                              }`}
                            >
                              {issue.level === 1 ? "NCT Issued" : "Avoided NCT"}
                            </td>
                            <td className="px-[6px] py-4 text-left text-sm">
                              {(() => {
                                const relevantInfo =
                                  issue.qualityAlertLocationInfo;

                                const hasParts = relevantInfo.some(
                                  (info) =>
                                    info.qualityAlertDiscrepantMaterial.length >
                                    0,
                                );

                                if (!hasParts) {
                                  return <span>N/A</span>;
                                }

                                return (
                                  <ul className="list-inside list-disc">
                                    {relevantInfo.flatMap((info, indexInfo) =>
                                      info.qualityAlertDiscrepantMaterial.map(
                                        (material, indexMaterial) => (
                                          <li
                                            key={`${material.part.id}_${indexInfo}_${indexMaterial}`}
                                          >
                                            {material.part.number}
                                          </li>
                                        ),
                                      ),
                                    )}
                                  </ul>
                                );
                              })()}
                            </td>
                            <td className="px-[6px] py-4 text-left text-sm underline">
                              {(() => {
                                return getLocationCount(issue);
                              })()}
                            </td>
                            <td className="px-[6px] py-4 text-left text-sm">
                              {issue.time
                                ? formatDate(
                                    issue.time ? new Date(issue.time) : null,
                                  )
                                : "N/A"}
                            </td>

                            <td className="flex h-full w-full items-center justify-center px-1.5 py-4 text-sm">
                              <span
                                className={`inline-block rounded-md px-8 py-1 text-sm ${
                                  issue.status === "Open"
                                    ? "bg-tqsLightRed font-semibold text-tqsRed"
                                    : "bg-tqsLightGreen font-bold text-tqsGreen"
                                }`}
                              >
                                {issue.status}
                              </span>
                            </td>
                            <td className="px-3 py-4 text-center text-[12px]">
                              <button className="ml-4 text-[14px] text-gray-500 hover:underline">
                                details
                              </button>
                            </td>
                          </tr>

                          {expandedRow === index && (
                            <tr>
                              <td colSpan={7} className="p-0">
                                <div
                                  ref={contentRef}
                                  style={{
                                    maxHeight:
                                      expandedRow === index ? maxHeight : "0px",
                                    overflow: "hidden",
                                    transition: "max-height 0.3s ease-out",
                                  }}
                                >
                                  <div className="p-4">
                                    <QualityAlertDetails
                                      allLocations={allLocations}
                                      issue={issue}
                                      index={index}
                                      expandedRow={expandedRow}
                                      updateQualityAlert={
                                        handleQualityAlertUpdate
                                      }
                                    />
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))
                    )}
                  </tbody>
                )}
              </table>
            </div>
          </div>
        </div>
        {
          <AddQualityAlertModal
            ref={modalRef}
            location={selectedLocation}
            allLocations={allLocations}
          >
            <div className="flex w-[100%] flex-col">
              <div className="pb-[4px]">
                <span className="text-[16px] font-semibold text-[black]">
                  Select OEMS
                </span>
              </div>
            </div>
          </AddQualityAlertModal>
        }
      </div>
    </>
  );
}
